import { z } from 'zod';

// Base schemas
export const mediaSchema = z.object({
    id: z.string().optional(),
    link: z.string().url().optional(),
    caption: z.string().optional(),
    filename: z.string().optional(),
});

export const textSchema = z.object({
    body: z.string().min(1, 'Message text is required'),
    font: z.enum([
        'SYSTEM',
        'SYSTEM_TEXT',
        'FB_SCRIPT',
        'SYSTEM_BOLD',
        'MORNINGBREEZE_REGULAR',
        'CALISTOGA_REGULAR',
        'EXO2_EXTRABOLD',
        'COURIERPRIME_BOLD',
    ]).optional(),
    background_color: z.string().regex(/^#[0-9A-F]{6}$/i).optional(),
    text_color: z.string().regex(/^#[0-9A-F]{6}$/i).optional(),
});

export const messageContentSchema = z.object({
    type: z.enum(['text', 'image', 'video', 'audio']),
    text: textSchema.optional(),
    image: mediaSchema.optional(),
    video: mediaSchema.optional(),
    audio: mediaSchema.optional(),
}).refine(
    (data) => {
        switch (data.type) {
            case 'text':
                return !!data.text;
            case 'image':
                return !!data.image;
            case 'video':
                return !!data.video;
            case 'audio':
                return !!data.audio;
            default:
                return false;
        }
    },
    {
        message: 'Content must match the specified type',
    }
).refine(
    (data) => {
        // If text is present, validate its required fields
        if (data.text) {
            return !!data.text.body;
        }
        return true;
    },
    {
        message: 'Content must match the specified type',
    }
);

// Request schemas
export const createAccountSchema = z.object({
    name: z.string().min(1, 'Account name is required'),
});

export const messageRequestSchema = z.object({
    content: messageContentSchema,
    schedule: z.string().datetime().optional(),
});

export const loginRequestSchema = z.object({
    email: z.string().email('Invalid email format'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
});

export const registerRequestSchema = z.object({
    email: z.string().email('Invalid email format'),
    name: z.string().min(1, 'Name is required'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    phone: z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format'),
});

export const updatePasswordSchema = z.object({
    old_password: z.string().min(6, 'Current password must be at least 6 characters'),
    new_password: z.string().min(6, 'New password must be at least 6 characters'),
});

// Response schemas
export const userSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().email(),
    phone: z.string(),
    active: z.boolean(),
    admin: z.boolean(),
    metadata: z.object({
        last_login: z.string().datetime().optional(),
    }).optional(),
    settings: z.object({
        endpoint: z.string().url().optional(),
        include_not_contacts: z.boolean().optional(),
    }).optional(),
    deleted_at: z.string().datetime().optional(),
});

export const accountSchema = z.object({
    id: z.string(),
    name: z.string(),
    external_id: z.string(),
    wa_id: z.string(),
    status: z.enum(['connected', 'disconnected', 'creating', 'initializing']),
    user_id: z.string(),
    user: userSchema.optional(),
    subscriptions: z.array(z.object({
        id: z.string(),
        account_id: z.string(),
        type: z.string(),
        status: z.string(),
        started_at: z.string().datetime(),
        expires_at: z.string().datetime(),
        metadata: z.record(z.unknown()).optional(),
        deleted_at: z.string().datetime().optional(),
    })).optional(),
    deleted_at: z.string().datetime().optional(),
});

export const messageSchema = z.object({
    id: z.number(),
    content: messageContentSchema,
    status: z.enum(['pending', 'scheduled', 'sent', 'failed', 'deleted']),
    scheduled: z.string().datetime().optional(),
    sent_at: z.string().datetime().optional(),
    external_id: z.string().optional(),
    account_id: z.string(),
    account: accountSchema.optional(),
    user_id: z.string(),
    user: userSchema.optional(),
    sent_by_id: z.string().optional(),
    metrics: z.object({
        delivery: z.number(),
        views: z.number(),
    }).optional(),
    deleted_at: z.string().datetime().optional(),
});

// Helper type inference
export type MessageRequestInput = z.infer<typeof messageRequestSchema>;
export type LoginRequestInput = z.infer<typeof loginRequestSchema>;
export type RegisterRequestInput = z.infer<typeof registerRequestSchema>;
export type UpdatePasswordInput = z.infer<typeof updatePasswordSchema>;
